import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Container, SectionHeading, PrimaryLink, SecondaryLink } from "@/components/ui-kit";
import { FAQS, getPlanPrice, getCombinedOwnershipTotal } from "@/data/content";
import { useRegion } from "@/hooks/useRegion";
import AdSlot from "@/components/ads/AdSlot";
import { isTagalogSelected } from "@/data/tagalogTranslations";

function FaqItem({ item, index, open, onToggle }) {
  return (
    <div data-testid={`faq-item-${index}`} className="border border-[#E2E8F0] rounded-2xl bg-white overflow-hidden transition-all">
      <button
        data-testid={`faq-trigger-${index}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-semibold font-display text-[#0F172A]">{item.q}</span>
        <span className={`shrink-0 rounded-full p-1.5 transition-colors ${open ? "bg-[#10B981] text-white" : "bg-[#F1F5F9] text-[#475569]"}`}>
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      <div className={`grid transition-all duration-300 ease-in-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <p data-testid={`faq-answer-${index}`} className="px-6 pb-5 text-[#475569] leading-relaxed">
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  const { region, isPH, link } = useRegion();
  const ownershipPrice = getPlanPrice("ownership", region);
  const addOnPrice = getPlanPrice("assisted_setup_launch", region);
  const combinedPrice = getCombinedOwnershipTotal(region);
  const isTL = isPH && isTagalogSelected();
  const faqs = FAQS.map((item) => {
    if (item.q === "If I'm on a care plan, do I own the website?") {
      return {
        ...item,
        a: isPH
          ? isTL
            ? "Hindi awtomatiko. Binabawasan ng mga ganap at kwalipikadong nabayarang buwan sa Responsibility o Priority plan ang opsyonal na presyo ng PH ownership conversion. Matapos ang 24 qualifying paid months, wala nang karagdagang presyo sa pagbili ng MySiteCare ownership, ngunit kailangan pa rin ang verification, pagbabayad ng natitirang balanse at third-party cost, nakasulat na kahilingan, at handover. Karaniwang hindi nakatatanggap ng conversion credit ang Maintenance Only."
            : "Not automatically. Qualifying fully paid Responsibility or Priority months reduce the optional PH ownership-conversion price. After 24 qualifying paid months, no additional MySiteCare ownership purchase price is due, but verification, outstanding balances, third-party costs, a written request, and handover still apply. Maintenance Only does not normally earn conversion credit."
          : "No. EU / international subscription payments are service fees and do not purchase, finance, progressively transfer, or automatically create website ownership rights. Ownership must be purchased and handed over separately under the Website Ownership option or another written transfer agreement.",
      };
    }
    if (item.q === "What is included in Website Ownership?") {
      // Always derived from the same PLAN_PRICING source of truth as the Plans pages —
      // never a second hardcoded price, so this can't drift the way the old bundled
      // "€900 / ₱64,000" wording did once Assisted Setup & Launch was unbundled.
      return {
        ...item,
        a: isTL
          ? `Ang Website Ownership ay ${ownershipPrice.price} ${ownershipPrice.period}. Kasama rito ang napagkasunduang standalone website package, kumpletong data export ng bawat business module na naka-enable, at dokumentasyon sa pag-setup—hindi kasama ang deployment. Ang Assisted Setup & Launch ay hiwalay at opsyonal na one-time add-on na nagkakahalaga ng ${addOnPrice.price} ${addOnPrice.period} kung nais ninyong MySiteCare ang humawak sa deployment (kabuuan: ${combinedPrice.price} ${combinedPrice.period}).`
          : `Website Ownership is ${ownershipPrice.price} ${ownershipPrice.period}. It includes the agreed standalone website package, a full data export of every business module you've enabled, and setup documentation — deployment is not included. Assisted Setup & Launch is a separate, optional one-time add-on for ${addOnPrice.price} ${addOnPrice.period} if you'd like MySiteCare to handle deployment (combined: ${combinedPrice.price} ${combinedPrice.period}).`,
      };
    }
    return item;
  });

  return (
    <div data-testid="faq-page">
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <Container className="py-16 md:py-20 text-center">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#10B981] mb-3">
            Clear answers. Clear boundaries.
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-[#0F172A] text-balance max-w-3xl mx-auto">
            Frequently Asked Questions
          </h1>
          <p className="mt-6 text-lg text-[#475569] max-w-2xl mx-auto">
            This page explains what MySiteCare does — and what it doesn't. If you want marketing promises, this isn't for
            you. If you want responsibility, it probably is.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5 justify-center">
            {["Ownership vs Responsibility", "No marketing guarantees", "Plan-based scope", "Calm, predictable service"].map((t) => (
              <span key={t} className="rounded-full border border-[#E2E8F0] bg-white px-4 py-1.5 text-xs font-medium text-[#475569]">
                {t}
              </span>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <Container className="max-w-4xl">
          <div className="space-y-4">
            {faqs.map((item, i) => (
              <FaqItem
                key={i}
                item={item}
                index={i}
                open={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>

          {/* Ad placement: long-form public informational content, well below the FAQ
              answers themselves and clearly separated by spacing/border from any
              MySiteCare action below it — see AdSlot.js for the full rendering rules
              (never renders anything on a sensitive route, dev/test always shows only
              a labelled placeholder). */}
          <div className="mt-12">
            <AdSlot placement="public_content_inline" />
          </div>

          <div className="mt-8 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] p-8 text-center">
            <p className="text-[#475569]">
              This FAQ is a practical summary. For full details, see our{" "}
              <a href={link("/clear-terms")} className="text-[#10B981] hover:underline">Payment Terms</a> and{" "}
              <a href={link("/terms-and-conditions")} className="text-[#10B981] hover:underline">Terms &amp; Conditions</a>.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
              <PrimaryLink to={link("/contact")} testId="faq-cta-contact">
                Need a direct answer? Contact us
              </PrimaryLink>
              <SecondaryLink to={link("/plans")} testId="faq-cta-plans">
                View Plans
              </SecondaryLink>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
